import { useEffect, useState } from 'react';
import { api } from '../api';
import { today } from '../dates';
import { Sheet } from './Sheet';

type Props = {
  onClose: () => void;
  onToast: (message: string, kind?: 'ok' | 'error') => void;
  onError: (err: unknown) => void;
};

const RANGES = [
  { days: 30, label: '直近1ヶ月' },
  { days: 60, label: '2ヶ月' },
  { days: 90, label: '3ヶ月' },
] as const;

const PROMPT =
  '以下は私の筋トレ記録です。全体の傾向と、次の1ヶ月で伸ばすべきポイントを教えてください。\n\n';

export function ExportSheet({ onClose, onToast, onError }: Props) {
  const [days, setDays] = useState<number>(30);
  const [markdown, setMarkdown] = useState('');
  const [withPrompt, setWithPrompt] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .exportMarkdown(today(), days)
      .then((r) => {
        if (!cancelled) setMarkdown(r.markdown);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [days, onError]);

  const payload = (withPrompt ? PROMPT : '') + markdown;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  /** スマホでは OS の共有シート経由で Claude アプリへ直接渡す */
  const send = async () => {
    if (canShare) {
      try {
        await navigator.share({ text: payload });
        return;
      } catch (err) {
        // ユーザーが共有シートを閉じただけなら何もしない
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // 共有が使えない環境ならコピーにフォールバック
      }
    }
    await copy();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      onToast('コピーしました — Claude に貼り付けてください');
    } catch {
      onToast('コピーできませんでした。下のテキストを手動で選択してください', 'error');
    }
  };

  return (
    <Sheet
      title={
        <div className="sheet__title">
          <span className="sheet__date">Claude へ送る</span>
          <span className="sheet__wd">{markdown ? `${payload.length.toLocaleString('ja-JP')} 文字` : ''}</span>
        </div>
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={copy} disabled={loading || !markdown}>
            コピー
          </button>
          <button className="btn btn--primary" onClick={send} disabled={loading || !markdown}>
            {canShare ? '共有 → Claude を選ぶ' : 'コピーして Claude へ'}
          </button>
        </>
      }
    >
      <div className="rangepick">
        {RANGES.map((r) => (
          <button
            key={r.days}
            className="chip"
            aria-pressed={days === r.days}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <label
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, fontSize: 13 }}
      >
        <input
          type="checkbox"
          checked={withPrompt}
          onChange={(e) => setWithPrompt(e.target.checked)}
          style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
        />
        分析を依頼する一文を先頭に付ける
      </label>

      <div className="preview">
        {loading ? '読み込み中…' : payload || '記録がありません'}
      </div>

      <p className="hint">
        {canShare
          ? 'スマホでは「共有」から Claude アプリを選ぶと、この内容がそのまま入力欄に入ります。'
          : 'この端末は共有シートに対応していません。コピーして Claude に貼り付けてください。'}
      </p>
    </Sheet>
  );
}
